import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';
import type { User, LoginRequest } from '../types';
import { logger } from '@/utils/logger';

function extractError(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    return String((detail as { message: unknown }).message);
  }
  return fallback;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  clearUser: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/login', credentials);
          const { user } = response.data;
          set({ user, isAuthenticated: true, isLoading: false, error: null });
        } catch (error: unknown) {
          set({
            error: extractError(error, 'Anmeldung fehlgeschlagen'),
            isLoading: false,
            isAuthenticated: false,
            user: null,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (error) {
          logger.error('Logout error:', error);
        } finally {
          set({ user: null, isAuthenticated: false, error: null });
        }
      },

      refreshUser: async () => {
        try {
          const response = await api.get('/auth/me');
          set({ user: response.data, isAuthenticated: true, error: null });
        } catch {
          set({ user: null, isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),

      // Von useAuthUser() (react-query) aus synchron gehalten — die Query ist
      // seit der Session-System-Angleichung die einzige autoritative Quelle,
      // ob die Sitzung noch gueltig ist; dieser Store bleibt nur noch fuer
      // Komponenten relevant, die synchron auf user/isAuthenticated zugreifen
      // wollen (z.B. MainLayout).
      setUser: (user: User) => set({ user, isAuthenticated: true, error: null }),
      clearUser: () => set({ user: null, isAuthenticated: false }),
    }),
    {
      name: 'staffclock-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
